import React from 'react'
import { useTranslation } from 'react-i18next'
import { Info } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { SUPPORT_CATEGORIES } from '@/types/support'
import type { SupportCategory, SupportModuleConfig, SupportSelection, SupportSlaUnit, SupportTarget } from '@/types/support'
import type { ModuleFieldComponentProps } from '@/types/space-wizard'
import { ResponderPicker, useResponderLists } from './ResponderPicker'
import { MAX_HINT, emptySelection, useTeamOptions, type SupportConfigErrors } from './supportUtils'

const NONE = '__none'
const DAYS = [1, 2, 3, 4, 5, 6, 0]

const Toggle = ({ id, label, hint, checked, disabled, onChange }: { id: string; label: string; hint: string; checked: boolean; disabled?: boolean; onChange: (v: boolean) => void }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="min-w-0 space-y-1">
      <label htmlFor={id} className="text-sm font-medium text-gray-700">{label}</label>
      <p className="text-xs text-gray-500">{hint}</p>
    </div>
    <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onChange} aria-label={label} className="data-[state=checked]:bg-primary" />
  </div>
)

const Section = ({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) => (
  <section className="space-y-3">
    <div>
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
    {children}
  </section>
)

const TargetInput = ({ label, target, disabled, onChange, testId }: { label: string; target: SupportTarget; disabled?: boolean; onChange: (t: SupportTarget) => void; testId: string }) => {
  const { t } = useTranslation('support')
  return (
    <div className="min-w-0 space-y-1">
      <span className="text-xs text-gray-600">{label}</span>
      <div className="flex gap-2">
        <Input type="number" min={1} value={Number.isFinite(target.value) ? target.value : ''} disabled={disabled} aria-label={label} data-testid={`${testId}-value`} className="w-20"
          onChange={(e) => onChange({ ...target, value: e.target.value === '' ? NaN : Number(e.target.value) })} />
        <Select value={target.unit} onValueChange={(unit) => onChange({ ...target, unit: unit as SupportSlaUnit })} disabled={disabled}>
          <SelectTrigger aria-label={`${label} (${t('config.sla.unit')})`} className="min-w-0 flex-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="hours">{t('config.sla.hours')}</SelectItem>
            <SelectItem value="days">{t('config.sla.days')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

/**
 * The Support setup (F-82 section 7.6): who answers (for the space and per category), the deadline of each
 * category, business hours, the urgent flag, the open limit, the place hint and the default assignee. Long enough
 * for a step of its own in the wizard (PADROES-UI section 14); "Configure space" draws this very component.
 */
export const SupportSpaceFields: React.FC<ModuleFieldComponentProps<SupportModuleConfig> & { errors?: SupportConfigErrors | null }> = ({ config, onChange, disabled = false, errors }) => {
  const { t } = useTranslation('support')
  const lists = useResponderLists()
  const team = useTeamOptions(true)
  const [custom, setCustom] = React.useState<Record<string, boolean>>({})
  const set = (patch: Partial<SupportModuleConfig>) => onChange({ ...config, ...patch })
  const setSla = (patch: Partial<SupportModuleConfig['sla']>) => set({ sla: { ...config.sla, ...patch } })
  const hours = config.sla.businessHours

  const setCategorySelection = (category: SupportCategory, selection: SupportSelection | null) => {
    const byCategory = { ...config.responders.byCategory }
    if (selection) byCategory[category] = selection
    else delete byCategory[category]
    set({ responders: { ...config.responders, byCategory } })
  }
  const isCustom = (category: SupportCategory) => custom[category] ?? Boolean(config.responders.byCategory[category])

  return (
    <div className="min-w-0 space-y-7">
      <Section title={t('config.responders.title')} hint={t('config.responders.hint')}>
        <ResponderPicker
          value={config.responders}
          onChange={(sel) => set({ responders: { ...config.responders, ...sel } })}
          lists={lists}
          disabled={disabled}
        />
        <details className="rounded-lg border border-gray-200 p-3">
          <summary className="cursor-pointer text-sm font-medium text-gray-700">{t('config.responders.byCategory')}</summary>
          <div className="mt-3 space-y-4">
            {SUPPORT_CATEGORIES.map((category) => (
              <div key={category} className="space-y-2">
                <Toggle id={`support-cat-${category}`} label={t(`category.${category}`)} hint={isCustom(category) ? t('config.responders.own') : t('config.responders.followsSpace')}
                  checked={isCustom(category)} disabled={disabled}
                  onChange={(on) => { setCustom((c) => ({ ...c, [category]: on })); setCategorySelection(category, on ? (config.responders.byCategory[category] ?? emptySelection()) : null) }} />
                {isCustom(category) && (
                  <ResponderPicker value={config.responders.byCategory[category] ?? emptySelection()} onChange={(sel) => setCategorySelection(category, sel)} lists={lists} disabled={disabled} />
                )}
              </div>
            ))}
          </div>
        </details>
      </Section>

      <Section title={t('config.sla.title')} hint={t('config.sla.hint')}>
        <Toggle id="support-sla-enabled" label={t('config.sla.enabled')} hint={t('config.sla.enabledHint')} checked={config.sla.enabled} disabled={disabled} onChange={(enabled) => setSla({ enabled })} />
        {config.sla.enabled && (
          <>
            <div className="space-y-2 rounded-lg bg-gray-50 p-3">
              <span className="text-sm font-medium text-gray-700">{t('config.sla.businessHours')}</span>
              <div className="flex flex-wrap gap-2" role="group" aria-label={t('config.sla.businessDays')}>
                {DAYS.map((day) => {
                  const on = hours.days.includes(day)
                  return (
                    <button key={day} type="button" aria-pressed={on} disabled={disabled} data-testid={`support-day-${day}`}
                      onClick={() => setSla({ businessHours: { ...hours, days: on ? hours.days.filter((d) => d !== day) : [...hours.days, day].sort() } })}
                      className={cn('rounded-lg px-3 py-1.5 text-sm transition-colors', on ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-100', disabled && 'cursor-not-allowed opacity-50')}>
                      {t(`config.sla.day.${day}`)}
                    </button>
                  )
                })}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
                <Input type="time" value={hours.start} disabled={disabled} aria-label={t('config.sla.start')} className="w-32" onChange={(e) => setSla({ businessHours: { ...hours, start: e.target.value } })} />
                <span>{t('config.sla.to')}</span>
                <Input type="time" value={hours.end} disabled={disabled} aria-label={t('config.sla.end')} className="w-32" onChange={(e) => setSla({ businessHours: { ...hours, end: e.target.value } })} />
              </div>
              <p className="text-xs text-gray-500">{t('config.sla.noHolidays')}</p>
              {errors?.businessHours && <p role="alert" className="text-xs text-red-600">{errors.businessHours}</p>}
            </div>
            <div className="space-y-3">
              {SUPPORT_CATEGORIES.map((category) => (
                <div key={category} className="grid gap-2 rounded-lg border border-gray-200 p-3 sm:grid-cols-[8rem_1fr_1fr] sm:items-end">
                  <span className="text-sm font-medium text-gray-900">{t(`category.${category}`)}</span>
                  <TargetInput label={t('config.sla.firstResponse')} target={config.sla.categories[category].firstResponse} disabled={disabled} testId={`support-sla-${category}-first`}
                    onChange={(firstResponse) => setSla({ categories: { ...config.sla.categories, [category]: { ...config.sla.categories[category], firstResponse } } })} />
                  <TargetInput label={t('config.sla.resolution')} target={config.sla.categories[category].resolution} disabled={disabled} testId={`support-sla-${category}-resolution`}
                    onChange={(resolution) => setSla({ categories: { ...config.sla.categories, [category]: { ...config.sla.categories[category], resolution } } })} />
                </div>
              ))}
              {errors?.sla && <p role="alert" className="text-xs text-red-600">{errors.sla}</p>}
              <p className="text-xs text-gray-500">{t('config.sla.daysNote')}</p>
            </div>
          </>
        )}
      </Section>

      <Section title={t('config.general.title')}>
        <Toggle id="support-urgent-flag" label={t('config.allowUrgentFlag.label')} hint={t('config.allowUrgentFlag.hint')} checked={config.allowUrgentFlag} disabled={disabled} onChange={(allowUrgentFlag) => set({ allowUrgentFlag })} />
        <div className="space-y-1">
          <label htmlFor="support-max-open" className="text-sm font-medium text-gray-700">{t('config.maxOpen.label')}</label>
          <Input id="support-max-open" type="number" min={0} max={1000} value={Number.isFinite(config.maxOpenPerMember) ? config.maxOpenPerMember : ''} disabled={disabled} className="w-28" aria-invalid={Boolean(errors?.maxOpenPerMember)}
            onChange={(e) => set({ maxOpenPerMember: e.target.value === '' ? NaN : Number(e.target.value) })} />
          <p className="text-xs text-gray-500">{t('config.maxOpen.hint')}</p>
          {errors?.maxOpenPerMember && <p role="alert" className="text-xs text-red-600">{errors.maxOpenPerMember}</p>}
        </div>
        <div className="space-y-1">
          <label htmlFor="support-location-hint" className="text-sm font-medium text-gray-700">{t('config.locationHint.label')}</label>
          <Input id="support-location-hint" value={config.locationHint} maxLength={MAX_HINT} disabled={disabled} placeholder={t('config.locationHint.placeholder')} aria-invalid={Boolean(errors?.locationHint)} onChange={(e) => set({ locationHint: e.target.value })} />
          <p className="text-xs text-gray-500">{t('config.locationHint.hint')}</p>
          {errors?.locationHint && <p role="alert" className="text-xs text-red-600">{errors.locationHint}</p>}
        </div>
        <div className="space-y-1">
          <span className="text-sm font-medium text-gray-700">{t('config.defaultAssignee.label')}</span>
          <Select value={config.defaultAssigneeId ?? NONE} onValueChange={(v) => set({ defaultAssigneeId: v === NONE ? null : v })} disabled={disabled}>
            <SelectTrigger aria-label={t('config.defaultAssignee.label')} data-testid="support-default-assignee"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>{t('config.defaultAssignee.none')}</SelectItem>
              {team.options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500">{t('config.defaultAssignee.hint')}</p>
          {errors?.defaultAssigneeId && <p role="alert" className="text-xs text-red-600">{errors.defaultAssigneeId}</p>}
        </div>
      </Section>

      <p className="flex items-start gap-2 rounded-lg bg-gray-50 p-3 text-xs text-gray-600"><Info className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />{t('config.privacy')}</p>
    </div>
  )
}
